import { Box, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import {
  Link,
  createFileRoute,
  notFound,
  rootRouteId,
  useRouter,
} from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import AuthLayout from "@/components/AuthLayout/AuthLayout";
import { useAuth } from "@/contexts/AuthContext";
import { authLinkStyles } from "@/routes/-authStyles";
import { DEMO_EMAIL, consumeDemoFragment } from "@/utils/demoLink";

const isDemoRouteEnabled = import.meta.env.VITE_FF_ENABLE_DEMO_ROUTE === "true";

export const Route = createFileRoute("/demo")({
  beforeLoad: () => {
    // A real kill switch, not a rollout flag. If DEMO_PASSWORD is ever emptied
    // (values-production.yaml contemplates it), /demo turns into a public URL
    // that runs a real WorkOS auth attempt on every load.
    if (!isDemoRouteEnabled) {
      throw notFound({ routeId: rootRouteId });
    }
  },
  head: () => ({ meta: [{ name: "robots", content: "noindex" }] }),
  // Deliberately no authenticated-user redirect: a prospect whose demo session
  // has expired still reads as `isAuthenticated`, and a login.tsx-style guard
  // would bounce them into a broken shell — the link would fail exactly when it
  // is needed most.
  component: DemoComponent,
});

type DemoStatus = "preparing" | "incomplete" | "failed";

export function DemoComponent() {
  const auth = useAuth();
  const router = useRouter();
  const [status, setStatus] = useState<DemoStatus>("preparing");
  // StrictMode double-invokes effects; one sign-in attempt only.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // Synchronous, before anything can await: stripping the fragment after the
    // login round trip would leave the password in the address bar (and in any
    // Sentry event raised meanwhile) for the length of a network call.
    const password = consumeDemoFragment();

    // No password means no network call at all — an empty one would fall
    // through the demo bypass into a real WorkOS auth attempt.
    if (!password) {
      setStatus("incomplete");
      return;
    }

    void (async () => {
      try {
        // Not logout(): that POSTs with a stale bearer and asks sphinx to clear
        // the refresh cookies moments before we ask for new ones. Resetting
        // first also retires an armed refresh timer left over from a previous
        // session, which would otherwise overwrite the demo token mid-demo. The
        // cost is that opening a demo link ends whatever real session this
        // browser had — the expected reading of the link.
        auth.resetSession();
        await auth.login({ email: DEMO_EMAIL, password });
        await router.invalidate();
        await router.navigate({ to: "/" });
      } catch (error: unknown) {
        // Never the server's error text: this page is read by prospects, not
        // operators. Report the status code only — the thrown axios error
        // carries the request body, password included.
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        Sentry.captureMessage(
          `Demo link sign-in failed${status ? ` (${status})` : ""}`,
          "warning",
        );
        setStatus("failed");
      }
    })();
  }, [auth, router]);

  if (status === "preparing") {
    // A visible in-flight state is required, not decorative: `appReady`
    // resolves after router.load(), which does not await this effect, so the
    // boot overlay is already gone by the time the login POST is in flight.
    return (
      <AuthLayout title="Preparing your demo…">
        <DemoMessage>Hang tight — we're signing you in.</DemoMessage>
      </AuthLayout>
    );
  }

  if (status === "incomplete") {
    return (
      <AuthLayout title="Demo link incomplete">
        <DemoMessage>
          This demo link is missing its access code. Ask whoever shared it for
          the full link.
        </DemoMessage>
        <Box component={Link} to="/login" sx={authLinkStyles}>
          Back to login
        </Box>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Demo unavailable">
      <DemoMessage>
        We couldn't start the demo just now. Try the link again, or get in touch
        and we'll sort it out.
      </DemoMessage>
      <Box component={Link} to="/login" sx={authLinkStyles}>
        Back to login
      </Box>
    </AuthLayout>
  );
}

function DemoMessage({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        color: "#fff",
        fontSize: "18px",
        fontWeight: 300,
        textAlign: "center",
      }}
    >
      {children}
    </Typography>
  );
}
